import { type RefObject, useLayoutEffect, useState } from 'react'

const DEFAULT_MIN_HEIGHT = 280
const DEFAULT_BOTTOM_GAP = 20

/**
 * Computes a scroll area height that fits between its current top position and viewport bottom.
 */
export function useViewportScrollAreaHeight(
  ref: RefObject<HTMLElement | null>,
  minHeight = DEFAULT_MIN_HEIGHT,
  bottomGap = DEFAULT_BOTTOM_GAP,
): number | null {
  const [height, setHeight] = useState<number | null>(null)

  useLayoutEffect(() => {
    if (typeof window === 'undefined') return

    let frame = 0
    const compute = (): void => {
      const element = ref.current
      if (!element) return

      const rect = element.getBoundingClientRect()
      const next = Math.max(
        minHeight,
        Math.floor(window.innerHeight - rect.top - bottomGap),
      )
      setHeight(prev => (prev === next ? prev : next))
    }

    const scheduleCompute = (): void => {
      cancelAnimationFrame(frame)
      frame = requestAnimationFrame(compute)
    }

    scheduleCompute()
    window.addEventListener('resize', scheduleCompute)
    window.addEventListener('scroll', scheduleCompute, true)

    return () => {
      cancelAnimationFrame(frame)
      window.removeEventListener('resize', scheduleCompute)
      window.removeEventListener('scroll', scheduleCompute, true)
    }
  }, [bottomGap, minHeight, ref])

  return height
}
